import { useEffect, useState } from "react";
import jwt_decode from "jwt-decode";
import usePageFocus from "./usePageFocus";
import { clearAuthetication, msUntilSessionMaxAge } from "./userData";

const MS_PER_SECOND = 1000;

// Refresh once, 10 minutes before expiry: enough headroom for the
// focus-retry rescue if the prompt is silently dropped, and a ~50-minute
// cadence stays well clear of FedCM's ~10-minute auto-reauthn quiet
// period, so refreshes remain fully silent.
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * MS_PER_SECOND;

const msUntilExpiry = (credential) =>
  jwt_decode(credential.id_token).exp * MS_PER_SECOND - new Date().valueOf();

export const isNearExpiry = (credential) =>
  msUntilExpiry(credential) <= REFRESH_BEFORE_EXPIRY_MS;

/*
 * Decides when a new id_token must be requested from Google.
 * While refreshTick > 0, the consumer must render a hidden
 * <GoogleLogin useOneTap auto_select key={refreshTick}> to fire the One Tap
 * prompt, then call clearRefresh() once the new credential arrives.
 */
const useSilentTokenRefresh = (credential) => {
  const [refreshTick, setRefreshTick] = useState(0);
  const isFocusPage = usePageFocus();

  useEffect(() => {
    if (!credential) return;

    // A new credential supersedes any pending refresh prompt (e.g. a token
    // adopted from another tab via the storage listener)
    setRefreshTick(0);

    const timeUntilExpiry = msUntilExpiry(credential);

    if (timeUntilExpiry <= 0) {
      // Token died while the app was closed — back to the login screen,
      // where One Tap auto_select signs the user in again almost instantly
      clearAuthetication(true);
      return;
    }

    const timer = setTimeout(
      () => setRefreshTick((tick) => tick + 1),
      Math.max(timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS, 0)
    );
    return () => clearTimeout(timer);
  }, [credential]);

  // Hard 7-day session cap: silent refreshes never extend it, and when it
  // lapses while the app is open the user is signed out on the spot
  // (cross-tab: removing the token fires the storage listener, so every
  // other tab reloads to the login screen as well)
  useEffect(() => {
    if (!credential) return;

    const timeUntilSessionMaxAge = msUntilSessionMaxAge(credential);

    if (timeUntilSessionMaxAge <= 0) {
      clearAuthetication(true);
      return;
    }

    const timer = setTimeout(
      () => clearAuthetication(true),
      timeUntilSessionMaxAge
    );
    return () => clearTimeout(timer);
  }, [credential]);

  // Prompts fired while the tab is hidden can be silently dropped by the
  // browser, so on every focus near/past expiry we bump the tick to remount
  // a fresh prompt
  useEffect(() => {
    if (!credential || !isFocusPage) return;
    if (isNearExpiry(credential)) {
      setRefreshTick((tick) => tick + 1);
    }
  }, [isFocusPage, credential]);

  const clearRefresh = () => setRefreshTick(0);

  return { refreshTick, clearRefresh };
};

export default useSilentTokenRefresh;
